import React from 'react';
import { motion } from 'framer-motion';
import { 
  ShieldCheck, 
  Activity, 
  RotateCcw, 
  Stethoscope, 
  TrendingUp, 
  Zap, 
  Target,
  HeartPulse
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import { FeatureCard } from '@/components/Cards';
import { IMAGES } from '@/assets/images';
import { ROUTE_PATHS, CONSTITUTION_TYPES } from '@/lib/index';
import { Link } from 'react-router-dom';

const springPresets = {
  gentle: { stiffness: 300, damping: 35 },
  smooth: { stiffness: 200, damping: 40 },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const About = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <img 
            src={IMAGES.KOREAN_MEDICINE_8} 
            alt="Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={springPresets.smooth}
            variants={fadeInUp}
            className="max-w-3xl"
          >
            <span className="inline-block px-3 py-1 mb-6 text-xs font-semibold tracking-wider text-primary uppercase bg-primary/10 rounded-full">
              About the System
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-8 leading-tight">
              사상체질 기반의 <br />
              <span className="text-primary">정밀 영양 관리 시스템</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              본 시스템은 사상체질 이론을 바탕으로 개인의 신체 특성과 섭취 반응을 구조화하여,
              체질에 맞는 단백질 선택과 지속적인 섭취 관리를 돕는 비의료적 지원 시스템입니다.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core Philosophy Section */}
      <section className="py-24 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
            >
              <h2 className="text-3xl font-bold mb-6">사상체질 이론 기반의 구조</h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                사상체질은 단순한 분류가 아닙니다. 장부의 허실(간·폐·비·신)의 상대적 균형과 기의 방향성(발산·수렴·저장·소모)을 이해함으로써,
                나의 몸이 에너지를 어떻게 처리하고 반응하는지를 과학적으로 분석합니다.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Object.values(CONSTITUTION_TYPES).map((type) => (
                  <div key={type.id} className="p-4 bg-background rounded-xl border border-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl font-bold text-primary">{type.element}</span>
                      <span className="font-semibold">{type.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground font-mono">
                      강: {type.organBalance.strong} / 약: {type.organBalance.weak}
                    </p>
                    <p className="text-xs mt-1 text-primary/80">{type.direction}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              className="relative rounded-3xl overflow-hidden shadow-2xl"
            >
              <img src={IMAGES.BODY_ANALYSIS_1} alt="Analysis" className="w-full aspect-square object-cover" />
              <div className="absolute inset-0 bg-primary/10 mix-blend-multiply" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Three Pillars Section */}
      <section className="py-24">
        <div className="container mx-auto px-4 text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">체질 분석의 세 가지 축</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            우리는 고정된 생리적 특성과 실시간 섭취 반응 데이터를 결합하여 완성도 높은 개인화 가이드를 제공합니다.
          </p>
        </div>
        <div className="container mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-8">
          <FeatureCard 
            title="장부 허실 및 기의 방향"
            description="사상체질 이론에 근거한 간·폐·비·신의 균형 상태와 기운의 흐름(발산/수렴)을 분석합니다."
            icon={<Activity className="w-6 h-6" />}
          />
          <FeatureCard 
            title="생활 및 섭취 패턴"
            description="식사량, 소화 불편 경험, 단백질 섭취 후의 신체 반응 및 활동량을 종합적으로 파악합니다."
            icon={<Target className="w-6 h-6" />}
          />
          <FeatureCard 
            title="실시간 섭취 피드백"
            description="제품 복용 지속성 및 주관적 체감 반응을 기록하여 관리 알고리즘을 고도화합니다."
            icon={<RotateCcw className="w-6 h-6" />}
          />
        </div>
      </section>

      {/* B2B Clinic Section */}
      <section className="py-24 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src={IMAGES.HEALTH_CONSULTATION_5} alt="Clinic" className="w-full h-full object-cover" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Stethoscope className="w-12 h-12 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl md:text-4xl font-bold mb-8">한의원 연계 건강관리 플랫폼</h2>
            <p className="text-lg md:text-xl opacity-90 mb-12 leading-relaxed">
              본 시스템은 한의원 환경에 최적화되어 있습니다. 한의사는 진료 후 생활·섭취 관리 도구로 활용하고, 
              환자는 일상에서 연속적인 회복 관리를 이어갈 수 있습니다.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              <div className="space-y-3">
                <div className="text-2xl font-bold font-mono">01</div>
                <p className="font-medium">진료실 내 전문 치료</p>
              </div>
              <div className="space-y-3">
                <div className="text-2xl font-bold font-mono">02</div>
                <p className="font-medium">진료실 외 회복 관리</p>
              </div>
              <div className="space-y-3">
                <div className="text-2xl font-bold font-mono">03</div>
                <p className="font-medium">지속적인 피드백 루프</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Differentiation & Future */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="p-8 bg-secondary/20 rounded-3xl border border-border/50">
              <div className="flex items-center gap-4 mb-6">
                <Zap className="w-8 h-8 text-primary" />
                <h3 className="text-2xl font-bold">기술적 차별성</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                기존 시스템이 단순 연령/성별 분류에 머물렀다면, 본 시스템은 '체질'이라는 고정 생리 특성과 '섭취 반응' 데이터를 결합합니다. 
                단순한 추천이 아닌 '왜 이 단백질이 나에게 맞는지'를 설명할 수 있는 과학적 근거를 제시합니다.
              </p>
            </div>
            <div className="p-8 bg-accent/20 rounded-3xl border border-border/50">
              <div className="flex items-center gap-4 mb-6">
                <TrendingUp className="w-8 h-8 text-primary" />
                <h3 className="text-2xl font-bold">확장 가능성</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                단백질 제품을 시작으로 체질 기반 건강기능식품, 맞춤 식이 가이드, 고령층 전용 관리 콘텐츠로 확장해 나갈 예정입니다. 
                단일 제품에 종속되지 않는 체질 기반 건강관리 플랫폼을 지향합니다.
              </p>
            </div>
          </div>

          <div className="mt-16 p-6 bg-muted rounded-xl flex items-start gap-4">
            <ShieldCheck className="w-6 h-6 text-muted-foreground flex-shrink-0 mt-1" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              본 시스템은 의학적 진단이나 치료, 효과 판정을 수행하지 않으며 한의원 진료를 대체하지 않는 비의료적 건강 관리 지원 도구입니다.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-background border-t border-border">
        <div className="container mx-auto px-4 text-center">
          <HeartPulse className="w-16 h-16 text-primary/40 mx-auto mb-8" />
          <h2 className="text-4xl font-bold mb-6">당신의 체질을 알고 싶으신가요?</h2>
          <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
            지금 바로 정밀 진단을 시작하고 나에게 꼭 맞는 단백질 섭취 계획을 세워보세요.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to={ROUTE_PATHS.DIAGNOSIS} 
              className="px-10 py-4 bg-primary text-primary-foreground rounded-full font-bold text-lg hover:scale-105 transition-transform shadow-xl"
            >
              체질 진단 시작하기
            </Link>
            <Link 
              to={ROUTE_PATHS.PRODUCTS} 
              className="px-10 py-4 bg-secondary text-secondary-foreground rounded-full font-bold text-lg hover:bg-accent transition-colors"
            >
              제품 라인업 보기
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;